/**
 * @name RestricaoAlimentarModal
 * @description Modal moderna para alertar sobre restrições alimentares
 * @author Keven Adan Bezerra | kevenadanb
 */

import useThemeContext from "@/themes/hooks/useThemeContext";
import {
  Close as CloseIcon,
  Restaurant as RestaurantIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import React from "react";

interface RestricaoAlimentarModalProps {
  open: boolean;
  onClose: () => void;
  produto: {
    nome: string;
    restricoes: string[];
  } | null;
  restricoesAluno: string[];
}

// Transição customizada para a modal
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const RestricaoAlimentarModal: React.FC<RestricaoAlimentarModalProps> = ({
  open,
  onClose,
  produto,
  restricoesAluno,
}) => {
  const { theme } = useThemeContext();

  if (!produto) return null;

  // Encontrar restrições conflitantes
  const restricoesConflitantes = produto.restricoes.filter((restricao) =>
    restricoesAluno.includes(restricao),
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
        },
      }}
    >
      {/* Header da Modal */}
      <DialogTitle
        sx={{
          bgcolor: theme.palette.error.main,
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <WarningIcon sx={{ fontSize: "2rem" }} />
          <Typography variant="h5" fontWeight="bold">
            Produto Restrito
          </Typography>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            color: "white",
            "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Conteúdo da Modal */}
      <DialogContent sx={{ p: 4 }}>
        {/* Ícone e mensagem principal */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <RestaurantIcon
            sx={{
              fontSize: "4rem",
              color: theme.palette.error.main,
              mb: 2,
            }}
          />

          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {produto.nome}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Este produto contém ingredientes que não são compatíveis com suas
            restrições alimentares.
          </Typography>
        </Box>

        {/* Restrições Conflitantes */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Ingredientes Conflitantes:
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {restricoesConflitantes.map((restricao, index) => (
              <Chip
                key={index}
                label={restricao}
                color="error"
                variant="filled"
                sx={{
                  fontWeight: "bold",
                  fontSize: "0.9rem",
                  "@keyframes pulse": {
                    "0%": {
                      boxShadow: "0 0 0 0 rgba(220, 38, 38, 0.7)",
                    },
                    "70%": {
                      boxShadow: "0 0 0 10px rgba(220, 38, 38, 0)",
                    },
                    "100%": {
                      boxShadow: "0 0 0 0 rgba(220, 38, 38, 0)",
                    },
                  },
                  animation: "pulse 2s infinite",
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Mensagem de segurança */}
        <Box
          sx={{
            p: 3,
            bgcolor: theme.palette.error.light + "20",
            borderRadius: 2,
            border: `2px solid ${theme.palette.error.light}`,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" fontWeight="medium">
            <strong>⚠️ Por sua segurança,</strong> este produto não pode ser
            adicionado ao seu carrinho.
            <br />
            Consulte outros produtos disponíveis em nosso cardápio.
          </Typography>
        </Box>
      </DialogContent>

      {/* Ações da Modal */}
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          fullWidth
          sx={{
            height: 48,
            fontSize: "1.1rem",
            fontWeight: "bold",
            textTransform: "none",
            borderRadius: 2,
          }}
        >
          Entendi, Escolher Outro Produto
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RestricaoAlimentarModal;
